import { useEffect, useState } from 'react';
import { systemAudit, type SystemAuditEntry, type PageMeta } from './system.api';
import { Panel, ErrorNote, inputClass } from './SystemLayout';

/**
 * What operators did. Append-only, and not touched by the 90-day log
 * archive that trims notifications, access_log and change_log - an
 * operator audit that expires is not an audit.
 */

/** Plain English for the stored action codes. */
const LABEL: Record<string, string> = {
  LOGIN: 'Signed in',
  LOGIN_FAILED: 'Failed sign-in',
  LOGIN_REFUSED_DEACTIVATED: 'Sign-in refused (deactivated)',
  PASSWORD_CHANGED: 'Changed their password',
  PASSWORD_CHANGE_FAILED: 'Failed password change',
  ORG_CREATED: 'Created an organisation',
  ORG_SUSPENDED: 'Suspended an organisation',
  ORG_RESTORED: 'Restored an organisation',
  SYSTEM_ADMIN_CREATED: 'Created an operator',
  SYSTEM_ADMIN_DEACTIVATED: 'Deactivated an operator',
  SYSTEM_ADMIN_REACTIVATED: 'Reactivated an operator',
};

/** Actions worth making visually obvious in a long list. */
const NOTABLE = new Set([
  'ORG_CREATED', 'ORG_SUSPENDED', 'SYSTEM_ADMIN_CREATED', 'SYSTEM_ADMIN_DEACTIVATED',
]);

export default function SystemAuditPage() {
  const [rows, setRows] = useState<SystemAuditEntry[]>([]);
  const [meta, setMeta] = useState<PageMeta | null>(null);
  const [action, setAction] = useState('');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const result = await systemAudit.list({
          action: action || undefined, page, pageSize: 50,
        });
        if (!cancelled) { setRows(result.rows); setMeta(result.meta); }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load the audit');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [action, page]);

  return (
    <div className="space-y-5">
      <ErrorNote message={error} />

      <Panel
        title={`Audit${meta ? ` (${meta.total})` : ''}`}
        action={
          <select
            value={action}
            onChange={(e) => { setAction(e.target.value); setPage(1); }}
            className={`${inputClass} mt-0 w-52`}
            aria-label="Filter by action"
          >
            <option value="">All actions</option>
            {Object.keys(LABEL).map((key) => (
              <option key={key} value={key}>{LABEL[key]}</option>
            ))}
          </select>
        }
      >
        {loading ? (
          <p className="text-sm text-slate-400">Loading...</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-400">Nothing recorded yet.</p>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 font-medium">When</th>
                  <th className="py-2 font-medium">Operator</th>
                  <th className="py-2 font-medium">Action</th>
                  <th className="py-2 font-medium">Detail</th>
                  <th className="py-2 font-medium">IP</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100 last:border-0 align-top">
                    <td className="whitespace-nowrap py-2.5 text-slate-400">
                      {new Date(row.createdAt).toLocaleString()}
                    </td>
                    <td className="py-2.5 text-slate-700">{row.adminEmail}</td>
                    <td className="py-2.5">
                      <span className={
                        NOTABLE.has(row.action)
                          ? 'text-amber-700'
                          : row.action.includes('FAILED') || row.action.includes('REFUSED')
                            ? 'text-red-600'
                            : 'text-slate-700'
                      }>
                        {LABEL[row.action] ?? row.action}
                      </span>
                    </td>
                    <td className="py-2.5 text-xs text-slate-500">
                      {row.detail ? <Detail value={row.detail} /> : '-'}
                    </td>
                    <td className="py-2.5 text-xs text-slate-500">{row.ip ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {meta && meta.totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between text-xs text-slate-400">
                <span>Page {meta.page} of {meta.totalPages}</span>
                <div className="flex gap-2">
                  <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                    className="rounded-lg border border-slate-300 px-2.5 py-1 transition hover:bg-slate-100 disabled:opacity-40">
                    Previous
                  </button>
                  <button type="button" disabled={page >= meta.totalPages} onClick={() => setPage((p) => p + 1)}
                    className="rounded-lg border border-slate-300 px-2.5 py-1 transition hover:bg-slate-100 disabled:opacity-40">
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Panel>
    </div>
  );
}

/**
 * The detail column is free-form JSON written by the API. Rendered as
 * key: value pairs rather than raw JSON, and never trusted to be any
 * particular shape - a row written by an older build must still display.
 */
function Detail({ value }: { value: unknown }) {
  if (typeof value !== 'object' || value === null) return <>{String(value)}</>;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (entries.length === 0) return <>-</>;
  return (
    <>
      {entries.map(([k, v]) => (
        <span key={k} className="mr-3 inline-block whitespace-nowrap">
          <span className="text-slate-400">{k}:</span> {String(v)}
        </span>
      ))}
    </>
  );
}
